/**
 * AIO生成モードのクレジット計算ユーティリティ
 */

// ベースクレジット（固定）
export const BASE_GENERATION_CREDITS = 30;

/**
 * 従量クレジット概算を計算（STEP1→2用）
 * スタイル設定の最小～最大見出し数から概算範囲を計算
 *
 * @param {Object} styleConfig - スタイル設定オブジェクト
 * @param {Object} styleOverrides - スタイルオーバーライド（微調整）
 * @returns {Object} { minCredits, maxCredits, explanation }
 */
export const calculateEstimatedUsageCredits = (
  styleConfig,
  styleOverrides = null,
) => {
  if (!styleConfig) {
    return {
      minCredits: 0,
      maxCredits: 0,
      explanation: 'スタイル設定が不明です',
    };
  }

  // オーバーライドがある場合は優先
  const baseHeadingsStyle = styleConfig?.headings_style;

  if (!baseHeadingsStyle || !baseHeadingsStyle.counts) {
    return {
      minCredits: 0,
      maxCredits: 0,
      explanation: '見出し数の設定が見つかりません',
    };
  }

  // オーバーライドがある場合はマージ
  let h2Counts = baseHeadingsStyle.counts.h2_per_article || {};
  let h3Counts = baseHeadingsStyle.counts.h3_per_h2 || {};

  if (styleOverrides?.headings_style?.counts) {
    if (styleOverrides.headings_style.counts.h2_per_article) {
      h2Counts = {
        ...h2Counts,
        ...styleOverrides.headings_style.counts.h2_per_article,
      };
    }
    if (styleOverrides.headings_style.counts.h3_per_h2) {
      h3Counts = {
        ...h3Counts,
        ...styleOverrides.headings_style.counts.h3_per_h2,
      };
    }
  }

  // typicalが範囲文字列（"4-5"）の場合は分割して数値化
  const parseRangeOrNumber = value => {
    if (!value) return null;
    if (typeof value === 'number') return value;
    const str = String(value);
    if (str.includes('-')) {
      const parts = str.split('-');
      return {
        min: parseInt(parts[0]),
        max: parseInt(parts[1]),
      };
    }
    return parseInt(str);
  };

  // H2の最小～最大
  let h2Min = 0;
  let h2Max = 0;

  if (h2Counts.min !== undefined && h2Counts.max !== undefined) {
    h2Min =
      typeof h2Counts.min === 'number'
        ? h2Counts.min
        : parseInt(h2Counts.min) || 0;
    h2Max =
      typeof h2Counts.max === 'number'
        ? h2Counts.max
        : parseInt(h2Counts.max) || 0;
  } else if (h2Counts.typical !== undefined) {
    const parsed = parseRangeOrNumber(h2Counts.typical);
    if (parsed && typeof parsed === 'object') {
      h2Min = parsed.min;
      h2Max = parsed.max;
    } else if (parsed) {
      h2Min = h2Max = parsed;
    }
  }

  // H3の最小～最大（H2あたり）
  let h3PerH2Min = 0;
  let h3PerH2Max = 0;

  if (h3Counts.min !== undefined && h3Counts.max !== undefined) {
    h3PerH2Min =
      typeof h3Counts.min === 'number'
        ? h3Counts.min
        : parseInt(h3Counts.min) || 0;
    h3PerH2Max =
      typeof h3Counts.max === 'number'
        ? h3Counts.max
        : parseInt(h3Counts.max) || 0;
  } else if (h3Counts.typical !== undefined) {
    const parsed = parseRangeOrNumber(h3Counts.typical);
    if (parsed && typeof parsed === 'object') {
      h3PerH2Min = parsed.min;
      h3PerH2Max = parsed.max;
    } else if (parsed) {
      h3PerH2Min = h3PerH2Max = parsed;
    }
  }

  // 最小クレジット計算（通常見出しとして計算）
  const minH2Credits = h2Min * 2; // 通常H2: 2クレジット
  const minH3Credits = h2Min * h3PerH2Min * 3; // 通常H3: 3クレジット
  const minCredits = minH2Credits + minH3Credits;

  // 最大クレジット計算（通常見出しとして計算）
  const maxH2Credits = h2Max * 2;
  const maxH3Credits = h2Max * h3PerH2Max * 3;
  const maxCredits = maxH2Credits + maxH3Credits;

  const explanation = `見出し数に応じて変動`;

  console.log('🔥 [CREDIT_CALC] Estimated usage credits:', {
    h2Counts,
    h3Counts,
    h2Min,
    h2Max,
    h3PerH2Min,
    h3PerH2Max,
    minCredits,
    maxCredits,
    styleOverrides,
  });

  return { minCredits, maxCredits, explanation };
};

/**
 * 従量クレジット確定値を計算（STEP4.1→4.2用）
 * 実際の見出し構成から正確に計算
 *
 * @param {Array} sections - 見出し構成配列
 * @returns {Object} { usageCredits, breakdown }
 */
export const calculateActualUsageCredits = sections => {
  if (!sections || sections.length === 0) {
    return { usageCredits: 0, breakdown: [] };
  }

  console.log(
    '🔥 [CREDIT_CALC] Input sections:',
    JSON.stringify(sections, null, 2),
  );

  let totalCredits = 0;
  const breakdown = [];

  // セクションを処理する再帰関数
  // parentLevelパラメータで親の見出しレベルを追跡
  const processSection = (section, parentLevel = null) => {
    // levelの判定ロジック：
    // 1. section.levelが明示的にある場合はそれを使用
    // 2. parentLevelがある場合（＝subsections内）はH3
    // 3. それ以外（トップレベル）はH2
    let level;
    if (section.level) {
      level = section.level;
    } else if (parentLevel === 'h2') {
      level = 'h3'; // H2の子はH3
    } else {
      level = 'h2'; // トップレベルはH2
    }

    // 独自情報判定ロジック：
    // H2: section.isExpansionSection === true（展開数はexpansionToppingから取得）
    // H3: section.type === 'topping'（toppingDataがある）
    const isExpansionH2 = section.isExpansionSection === true;
    const isToppingH3 = section.type === 'topping';
    const isUniqueInfo = isExpansionH2 || isToppingH3;

    // 展開数の取得（独自情報H2の場合）
    // 展開数 = section.autoExpansion.count
    let expansionCount = 0;
    if (isExpansionH2) {
      expansionCount = section.autoExpansion?.count || 0;

      console.log('🔥 [CREDIT_CALC] Expansion H2 detected:', {
        title: section.title,
        autoExpansion: section.autoExpansion,
        expansionCount,
      });
    }

    console.log('🔥 [CREDIT_CALC] Processing section:', {
      title: section.title,
      level,
      parentLevel,
      isExpansionH2,
      isToppingH3,
      isUniqueInfo,
      expansionCount,
      type: section.type,
      hasExpansionTopping: !!section.expansionTopping,
      hasToppingData: !!section.toppingData,
      hasSubsections: !!(section.subsections && section.subsections.length > 0),
      subsectionsCount: section.subsections?.length || 0,
    });

    let credits = 0;

    if (level === 'h2') {
      if (isUniqueInfo) {
        credits = 2 + expansionCount * 5;
        breakdown.push({
          title: section.title,
          level: 'H2',
          type: '独自情報',
          expansion: expansionCount,
          credits,
        });
      } else {
        credits = 2;
        breakdown.push({
          title: section.title,
          level: 'H2',
          type: '通常',
          credits,
        });
      }
    } else if (level === 'h3') {
      if (isUniqueInfo) {
        credits = 5;
        breakdown.push({
          title: section.title,
          level: 'H3',
          type: '独自情報',
          credits,
        });
      } else {
        credits = 3;
        breakdown.push({
          title: section.title,
          level: 'H3',
          type: '通常',
          credits,
        });
      }
    }
    // H4はクレジット消費なし（creditsは0のまま）

    totalCredits += credits;

    // 子セクションを再帰的に処理（現在のlevelを親として渡す）
    if (section.subsections && section.subsections.length > 0) {
      console.log(
        '🔥 [CREDIT_CALC] Processing subsections for:',
        section.title,
      );
      section.subsections.forEach(sub => processSection(sub, level));
    }
  };

  // トップレベルのセクションを処理（parentLevelはnull）
  sections.forEach(section => processSection(section, null));

  console.log('🔥 [CREDIT_CALC] Actual usage credits:', {
    totalCredits,
    breakdown,
  });

  return { usageCredits: totalCredits, breakdown };
};

/**
 * 合計クレジット計算（ベース + 従量）
 *
 * @param {number} baseCredits - ベースクレジット（30）
 * @param {number} usageCredits - 従量クレジット
 * @returns {number} 合計クレジット
 */
export const calculateTotalCredits = (baseCredits, usageCredits) => {
  return baseCredits + usageCredits;
};
